import React, { useCallback, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import AppTabs from '../../../components/AppTabs';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ptBR } from 'date-fns/locale';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { PosicaoAtualAtivoItem } from '../../../types/carteiraDiaria';
import { AtivoMaster, CronogramaAmortizacao } from '../../../types/ativoMaster';
import { getCronogramasByAtivoMaster } from '../../../services/ativoMasterService';
import { formatBRL, formatDate, formatRentSimples } from '../../../utils/formatters';
import { calcExpectedReturn, formatCdiPct, formatCdiSpread } from '../../../utils/expectedReturn';
import { useDatasDisponiveis } from '../../../hooks/useDatasDisponiveis';
import { usePosicaoAtualFundo } from '../../../hooks/usePosicaoAtualFundo';
import AtivoTableRow, { NUM_COLS } from '../components/posicao/AtivoTableRow';
import AssetGroupCard, { AssetGroup } from '../components/posicao/AssetGroupCard';
import RentabilidadeComparativaTab from '../components/posicao/RentabilidadeComparativaTab';
import CronogramaCalendarView from '../components/posicao/CronogramaCalendarView';
import HistoricoFundoTab from '../components/posicao/HistoricoFundoTab';
import MovimentacoesCotistaTab from '../components/posicao/MovimentacoesCotistaTab';
import TabPanel from '../../../components/TabPanel';
import CondominioBreakdownSection, { CondominioFiltro } from '../components/posicao/CondominioBreakdownSection';
import CadastrarAtivoMasterModal from '../../../components/CadastrarAtivoMasterModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type SortDir   = 'desc' | 'asc';
type SortField = 'nome' | 'concentracao';

// ─── Styles ──────────────────────────────────────────────────────────────────

const thSx = {
  backgroundColor: '#F4F7F9',
  color: '#4D5766',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  borderBottom: '2px solid #CFD8E5',
  whiteSpace: 'normal' as const,
  overflow: 'visible' as const,
  lineHeight: 1.3,
};

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '98%',
  maxWidth: '1600px',
  maxHeight: '92vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
  borderRadius: 2,
};

// ─── Grouping ────────────────────────────────────────────────────────────────

/**
 * Monitoramento de rentabilidade esperada — exclusivo para fundos gerenciados com segmento FIDC.
 *
 * Objetivo: detectar ativos cujo retorno diário realizado desvia significativamente
 * da taxa de remuneração contratual cadastrada no AtivoMaster (via De-Para).
 *
 * Funciona junto com o campo `rent_esperada_dia` retornado pelo endpoint
 * `/ativos/historico-ativo/`, que usa a mesma lógica para popular a coluna
 * "Rent. Esperada" na aba Histórico do ativo.
 *
 * A guarda `segmento === 'FIDC'` garante que o cálculo nunca acontece para
 * fundos de outros segmentos, mesmo que um AtivoMaster não-FIDC eventualmente
 * tenha rent_tipo preenchido.
 *
 * Para alterar a tolerância, edite DESVIO_TOLERANCIA abaixo.
 */

/**
 * Calcula a rentabilidade diária esperada em log-return para um ativo,
 * usando os campos de remuneração cadastrados no AtivoMaster.
 *
 * Três modalidades suportadas (mesmo padrão do backend RentabilidadeAtivoView):
 *   FIXO        — taxa fixa anual (AA) ou mensal (AM) convertida para diária
 *   IND_PERCENT — percentual de indicador (ex: 110% do CDI)
 *   IND_SPREAD  — indicador + spread fixo anual (ex: CDI + 2% a.a.)
 *
 * Retorna null quando:
 *   - o ativo não tem rent_tipo/rent_valor cadastrado
 *   - para IND_PERCENT/IND_SPREAD: o valor do indicador do dia não está disponível
 *     em indicadores_diarios (requer sync_indicadores atualizado no backend)
 */
const calcRentEsperadaDia = (
  ativo: PosicaoAtualAtivoItem,
  indicadores: Record<string, number> | null | undefined,
): number | null => {
  if (!ativo.rent_tipo || ativo.rent_valor == null) return null;
  const valor = ativo.rent_valor / 100;
  if (ativo.rent_tipo === 'FIXO') {
    const periodo = ativo.rent_periodo ?? 'AA';
    const daily = periodo === 'AA'
      ? Math.pow(1 + valor, 1 / 252) - 1
      : Math.pow(1 + valor, 1 / 21) - 1;
    return Math.log1p(daily);
  }
  if (!indicadores || !ativo.rent_indicador_codigo) return null;
  const indLn = indicadores[ativo.rent_indicador_codigo];
  if (indLn == null) return null;
  if (ativo.rent_tipo === 'IND_PERCENT') return indLn * valor;
  if (ativo.rent_tipo === 'IND_SPREAD')  return indLn + Math.log1p(valor) / 252;
  return null;
};

// Epsilon para comparação de log-returns: diferença acima de ~0.0001% em retorno simples aciona o alerta.
const DESVIO_EPSILON = 1e-6;

const computeGroups = (
  ativos: PosicaoAtualAtivoItem[],
  sortField: SortField,
  sortDir: SortDir,
  indicadores: Record<string, number> | null | undefined,
  segmento: string | null | undefined,
): AssetGroup[] => {
  const groupMap = new Map<string, AssetGroup>();

  ativos.forEach((ativo, flatIndex) => {
    let key: string;
    let label: string;
    let subLabel: string | undefined;
    let isMapped = ativo.tem_cadastro;

    if (ativo.tem_cadastro && ativo.grupo_fundo_id != null) {
      key = `grupo_${ativo.grupo_fundo_id}`;
      label = ativo.grupo_fundo_nome ?? 'Grupo sem nome';
      subLabel = ativo.grupo_fundo_cnpj ?? undefined;
    } else if (ativo.tem_cadastro && ativo.ativo_master_id != null) {
      key = `master_${ativo.ativo_master_id}`;
      label = ativo.nome_canonico ?? ativo.nome;
      isMapped = true;
    } else {
      const digits = (ativo.codigo1 ?? '').replace(/\D/g, '');
      if (digits.length === 14) {
        key = `cnpj_${ativo.codigo1}`;
        label = ativo.nome;
        subLabel = ativo.codigo1 ?? undefined;
        isMapped = false;
      } else {
        key = 'sem_grupo';
        label = 'Sem agrupamento';
        isMapped = false;
      }
    }

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        key, label, subLabel,
        grupoFundoId: ativo.tem_cadastro && ativo.grupo_fundo_id != null ? ativo.grupo_fundo_id : undefined,
        totalConcentracao: 0, totalValorFinanceiro: 0, totalVariacaoFinanceira: 0,
        items: [], isMapped,
        hasVariacaoFinanceira: false,
        hasAtivosNegativos: false, ativosNegativosNomes: [],
        hasDesvioEsperado: false, ativosDesvioNomes: [],
      });
    }
    const g = groupMap.get(key)!;

    // Só avalia desvio em fundos FIDC. Para outros segmentos, rentEsperada fica null
    // e nenhum flag é setado — evita falsos positivos em ativos de renda variável, etc.
    const rentEsperada = segmento === 'FIDC' ? calcRentEsperadaDia(ativo, indicadores) : null;
    let itemHasDesvio = false;
    if (rentEsperada != null && rentEsperada !== 0 && ativo.variacao_financeira != null && ativo.valor_total) {
      // Aproximação da rent diária via ln(VT_hoje / VT_ontem).
      // Assume que a quantidade não mudou no dia — válido para FIDCs sem aporte/resgate intraday.
      const valorAnterior = ativo.valor_total - ativo.variacao_financeira;
      if (valorAnterior > 0) {
        const rentAtual = Math.log(ativo.valor_total / valorAnterior);
        if (Math.abs(rentAtual - rentEsperada) > DESVIO_EPSILON) {
          itemHasDesvio = true;
          g.hasDesvioEsperado = true;
          g.ativosDesvioNomes.push(ativo.nome_canonico ?? ativo.nome);
        }
      }
    }

    g.items.push({ ativo, flatIndex, hasDesvio: itemHasDesvio });
    g.totalConcentracao += ativo.concentracao ?? 0;
    g.totalValorFinanceiro += ativo.valor_total ?? 0;
    g.totalVariacaoFinanceira += ativo.variacao_financeira ?? 0;
    if (ativo.variacao_financeira != null) g.hasVariacaoFinanceira = true;
    // Alerta de rent negativa — usa rent_cota_corrigida como fonte de verdade.
    // rent_cota_corrigida = (cota_hoje − cota_ontem + amort_por_cota) / cota_ontem
    // Um FIDC que pagou amortização tem cota_hoje < cota_ontem mas amort_por_cota
    // compensa a queda — a rent corrigida é positiva e o alerta não é acionado.
    // Fallback: variacao_financeira / PL_antes quando rent_cota_corrigida não disponível.
    const rentParaAlerta = ativo.rent_cota_corrigida ?? (
      ativo.variacao_financeira != null && ativo.valor_total != null && (ativo.valor_total - ativo.variacao_financeira) !== 0
        ? ativo.variacao_financeira / (ativo.valor_total - ativo.variacao_financeira)
        : null
    );
    if (rentParaAlerta != null && rentParaAlerta <= 0) {
      g.hasAtivosNegativos = true;
      g.ativosNegativosNomes.push(ativo.nome_canonico ?? ativo.nome);
    }
  });

  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'nome') return mul * a.label.localeCompare(b.label, 'pt-BR');
    return mul * (a.totalConcentracao - b.totalConcentracao);
  });

  return groups;
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface CarteiraPosicaoModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  fundoId: number;
  fundoNome: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const CarteiraPosicaoModal: React.FC<CarteiraPosicaoModalProps> = ({
  isOpen,
  onRequestClose,
  fundoId,
  fundoNome,
}) => {
  const [mainTab, setMainTab] = useState<'carteira' | 'rentabilidade' | 'historico' | 'movimentacoes' | 'cronograma'>('carteira');
  const [rentTabVisited, setRentTabVisited] = useState(false);
  const [cronogramaMap, setCronogramaMap] = useState<{ [masterId: number]: CronogramaAmortizacao[] }>({});
  const [loadingCronogramas, setLoadingCronogramas] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [grouped, setGrouped] = useState(false);
  const [sortField, setSortField] = useState<SortField>('concentracao');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [filtroTexto, setFiltroTexto] = useState('');
  const [condominioFiltro, setCondominioFiltro] = useState<CondominioFiltro>(null);

  const [cadastroModalOpen, setCadastroModalOpen] = useState(false);
  const [ativoParaCadastrar, setAtivoParaCadastrar] = useState<PosicaoAtualAtivoItem | null>(null);

  // Hooks
  const { loading: loadingDatas, shouldDisableDate, toLocalIso, toDatePickerValue } =
    useDatasDisponiveis(fundoId, isOpen);

  const { data, loading, error, setData } = usePosicaoAtualFundo(fundoId, isOpen, selectedDate);

  // Reset on close
  React.useEffect(() => {
    if (!isOpen) {
      setExpandedRowKey(null);
      setExpandedGroups(new Set());
      setMainTab('carteira');
      setRentTabVisited(false);
      setSelectedDate('');
      setFiltroTexto('');
      setCondominioFiltro(null);
      setCronogramaMap({});
    }
  }, [isOpen]);

  const handleMainTabChange = useCallback((_: React.SyntheticEvent, value: 'carteira' | 'rentabilidade' | 'historico' | 'movimentacoes' | 'cronograma') => {
    setMainTab(value);
    if (value === 'rentabilidade') setRentTabVisited(true);
  }, []);


  // Carrega cronogramas dos ativos FECHADO quando a aba é aberta
  React.useEffect(() => {
    if (mainTab !== 'cronograma' || !data) return;
    const uniqueIds: number[] = [];
    const seen: { [k: number]: boolean } = {};
    data.ativos.forEach(a => {
      if (a.condominio === 'FECHADO' && a.ativo_master_id != null && !seen[a.ativo_master_id]) {
        seen[a.ativo_master_id] = true;
        uniqueIds.push(a.ativo_master_id);
      }
    });
    if (uniqueIds.length === 0) { setCronogramaMap({}); return; }
    setLoadingCronogramas(true);
    Promise.all(uniqueIds.map(id => getCronogramasByAtivoMaster(id)))
      .then(results => {
        const m: { [k: number]: CronogramaAmortizacao[] } = {};
        uniqueIds.forEach((id, i) => { m[id] = results[i]; });
        setCronogramaMap(m);
      })
      .finally(() => setLoadingCronogramas(false));
  }, [mainTab, data]);

  const handleToggleRow = useCallback((key: string) => {
    setExpandedRowKey(prev => (prev === key ? null : key));
  }, []);

  const handleToggleGrouped = () => {
    setGrouped(prev => !prev);
    setExpandedRowKey(null);
    setExpandedGroups(new Set());
  };

  const handleToggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleAbrirCadastro = (ativo: PosicaoAtualAtivoItem) => {
    setAtivoParaCadastrar(ativo);
    setCadastroModalOpen(true);
  };

  const handleCadastroSalvo = (ativoMaster: AtivoMaster) => {
    if (!data) return;
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        ativos: prev.ativos.map((a: PosicaoAtualAtivoItem) => {
          if (
            a.adm === ativoParaCadastrar?.adm &&
            a.codigo1 === ativoParaCadastrar?.codigo1 &&
            a.nome === ativoParaCadastrar?.nome
          ) {
            return {
              ...a,
              tem_cadastro: true,
              ativo_master_id: ativoMaster.id,
              nome_canonico: ativoMaster.nome_canonico,
              classe_fidc_display: ativoMaster.classe_fidc_display,
              subclasse: ativoMaster.subclasse ?? undefined,
            };
          }
          return a;
        }),
      };
    });
  };

  const filteredAtivos = useMemo(() => {
    if (!data) return [];
    let result = data.ativos;

    if (condominioFiltro) {
      result = result.filter(a => {
        if (condominioFiltro === 'ABERTO')  return a.condominio === 'ABERTO';
        if (condominioFiltro === 'FECHADO') return a.condominio === 'FECHADO';
        return a.condominio !== 'ABERTO' && a.condominio !== 'FECHADO';
      });
    }

    const q = filtroTexto.trim().toLowerCase();
    if (!q) return result;
    return result.filter(a =>
      (a.codigo1 ?? '').toLowerCase().includes(q) ||
      a.nome.toLowerCase().includes(q) ||
      (a.nome_canonico ?? '').toLowerCase().includes(q),
    );
  }, [data, filtroTexto, condominioFiltro]);

  const groups = useMemo(
    () => grouped && data ? computeGroups(filteredAtivos, sortField, sortDir, data.indicadores_diarios, data.segmento) : [],
    [grouped, filteredAtivos, sortField, sortDir, data],
  );

  // ── Rentabilidade do fundo inteiro ────────────────────────────────────────────
  //
  // Preferência: rent_fundo_corrigida = (vpu_hoje − vpu_ontem) / vpu_ontem
  //   - vpu = valor por cota oficial do administrador (CarteiraDiariaPatrimonioLiquido)
  //   - Captura tudo automaticamente: amortizações, juros, taxas, caixa
  //   - Não precisa de ajuste manual — o administrador já consolida no NAV diário
  //
  // Fallback: Σ variacao_financeira / (PL − Σ variacao_financeira)
  //   - Usa qtd_hoje × (cota_hoje − cota_ontem) por ativo — distorcido por amortizações
  //   - Usado quando vpu_fundo não está disponível na tabela de PL
  //
  // BRL: rent × PL_ontem = rent × PL_hoje / (1 + rent)
  const carteiraMetrics = useMemo(() => {
    if (!data) return null;
    const hasVarData = data.ativos.some(a => a.variacao_financeira != null);
    if (!hasVarData && data.rent_fundo_corrigida == null) return null;
    const varTotal = data.ativos.reduce((sum, a) => sum + (a.variacao_financeira ?? 0), 0);
    const rentPct = data.rent_fundo_corrigida != null
      ? data.rent_fundo_corrigida
      : (data.patrimonio_liquido - varTotal) !== 0
        ? varTotal / (data.patrimonio_liquido - varTotal)
        : null;
    const varBrl = data.rent_fundo_corrigida != null && rentPct != null
      ? rentPct * (data.patrimonio_liquido / (1 + rentPct))
      : varTotal;
    return { varTotal: varBrl, rentPct };
  }, [data]);

  const rentEsperada = useMemo(() => {
    if (!data || data.segmento !== 'FIDC') return null;
    const indicadoresDiarios = data.indicadores_diarios;
    if (!indicadoresDiarios) return null;

    const totalAtivos = data.ativos.length;
    const withExp = data.ativos
      .map(a => {
        const ret = calcExpectedReturn(a, indicadoresDiarios);
        if (!ret) return null;
        return { daily: ret.daily, valor: a.valor_total ?? 0, var: a.variacao_financeira ?? 0 };
      })
      .filter((x): x is { daily: number; valor: number; var: number } => x !== null);

    const totalValorAll = data.ativos.reduce((s, a) => s + (a.valor_total ?? 0), 0);
    if (totalValorAll === 0 || withExp.length === 0) return null;
    const rentPct = withExp.reduce((s, x) => s + x.daily * x.valor, 0) / totalValorAll;
    const cdiDiario = indicadoresDiarios['CDI'];
    const cdiPct = cdiDiario != null && cdiDiario !== 0 ? rentPct / cdiDiario : null;
    const plAntesAll = data.ativos.reduce((s, a) => s + ((a.valor_total ?? 0) - (a.variacao_financeira ?? 0)), 0);
    const rentBrl = plAntesAll !== 0 ? rentPct * plAntesAll : null;
    return { rentPct, rentBrl, cdiPct, ativosComExp: withExp.length, totalAtivos };
  }, [data]);

  const fechadoNomes = useMemo(() => {
    const m: { [k: number]: string } = {};
    if (!data) return m;
    data.ativos.forEach(a => {
      if (a.condominio === 'FECHADO' && a.ativo_master_id != null && !m[a.ativo_master_id]) {
        m[a.ativo_master_id] = a.nome_canonico ?? a.nome;
      }
    });
    return m;
  }, [data]);

  const fechadoDetails = useMemo(() => {
    const m: { [k: number]: { nome: string; classeDisplay?: string; subclasse?: string } } = {};
    if (!data) return m;
    data.ativos.forEach(a => {
      if (a.condominio === 'FECHADO' && a.ativo_master_id != null && !m[a.ativo_master_id]) {
        m[a.ativo_master_id] = {
          nome:         a.nome_canonico ?? a.nome,
          classeDisplay: a.classe_fidc_display,
          subclasse:    a.subclasse,
        };
      }
    });
    return m;
  }, [data]);

  const rentColor = (value: number | null): string => {
    if (value == null || value === 0) return '#8A98A8';
    return value > 0 ? '#29A152' : '#E51739';
  };

  const handleDatePickerChange = (date: Date | null) => {
    setSelectedDate(date ? toLocalIso(date) : '');
    setExpandedRowKey(null);
  };

  return (
    <>
      <Modal open={isOpen} onClose={onRequestClose}>
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ fontFamily: 'Inter', fontWeight: 700, mb: 0.5 }}>
            {fundoNome}
          </Typography>
          <Divider sx={{ mb: 1 }} />

          {/* Top-level tabs */}
          <AppTabs value={mainTab} onChange={handleMainTabChange} sx={{ mb: 2 }}>
            <Tab label="Carteira Diária" value="carteira" />
            <Tab label="Rentabilidade vs Indicadores" value="rentabilidade" />
            <Tab label="Histórico" value="historico" />
            <Tab label="Movimentações" value="movimentacoes" />
            <Tab label="Cronograma" value="cronograma" />
          </AppTabs>

          {/* ── Tab: Carteira Diária ─────────────────────────────────────── */}
          <TabPanel value={mainTab} index="carteira">
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
              )}

              {data && !loading && (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1.5}>
                    {/* Controles */}
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                        <DatePicker
                          label="Data"
                          value={toDatePickerValue(selectedDate)}
                          onChange={handleDatePickerChange}
                          shouldDisableDate={shouldDisableDate}
                          disabled={loadingDatas}
                          slotProps={{
                            textField: { size: 'small', sx: { width: 195 } },
                            field: { clearable: true },
                          }}
                        />
                      </LocalizationProvider>
                      <TextField
                        size="small"
                        placeholder="Filtrar por nome ou código…"
                        value={filtroTexto}
                        onChange={e => setFiltroTexto(e.target.value)}
                        sx={{ width: 240 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: '#8A98A8' }} />
                            </InputAdornment>
                          ),
                          endAdornment: filtroTexto ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setFiltroTexto('')} edge="end">
                                <ClearIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                      <Button
                        variant={grouped ? 'contained' : 'outlined'}
                        size="small"
                        onClick={handleToggleGrouped}
                        startIcon={<LayersIcon />}
                        sx={{ fontFamily: 'Inter', fontSize: '13px', textTransform: 'none' }}
                      >
                        Agrupar
                      </Button>
                      {grouped && (
                        <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #CFD8E5', borderRadius: 1, overflow: 'hidden' }}>
                          {(['concentracao', 'nome'] as SortField[]).map((field, idx) => {
                            const isActive = sortField === field;
                            const label = field === 'concentracao' ? 'Concentração' : 'Nome';
                            const Icon = sortDir === 'desc' ? ArrowDownwardIcon : ArrowUpwardIcon;
                            return (
                              <Button
                                key={field}
                                size="small"
                                onClick={() => {
                                  if (isActive) setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
                                  else { setSortField(field); setSortDir('desc'); }
                                }}
                                endIcon={isActive ? <Icon sx={{ fontSize: '13px !important' }} /> : undefined}
                                sx={{
                                  fontFamily: 'Inter', fontSize: '12px', textTransform: 'none',
                                  borderRadius: 0, border: 'none', px: 1.5, py: 0.6,
                                  borderRight: idx === 0 ? '1px solid #CFD8E5' : 'none',
                                  backgroundColor: isActive ? '#EEF4FC' : 'transparent',
                                  color: isActive ? '#1967D2' : '#4D5766',
                                  fontWeight: isActive ? 600 : 400,
                                  '&:hover': { backgroundColor: isActive ? '#D2E3FC' : '#F4F7F9' },
                                }}
                              >
                                {label}
                              </Button>
                            );
                          })}
                        </Box>
                      )}
                    </Stack>

                    {/* Info: posição e PL */}
                    <Stack direction="row" spacing={0} alignItems="stretch"
                      sx={{ border: '1px solid #E2EAF2', borderRadius: 1.5, overflow: 'hidden' }}
                    >
                      <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                        <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                          Posição em
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2D3748', fontFamily: 'Inter', lineHeight: 1.4 }}>
                          {formatDate(data.data_posicao)}
                        </Typography>
                      </Box>
                      {carteiraMetrics && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                                Rent. do Dia
                              </Typography>
                              <Tooltip
                                title="Calculado como: quantidade × (preço hoje − preço ontem), somado para todos os ativos. O percentual é sobre o patrimônio líquido do início do dia. Ativos sem preço anterior (posições novas) não são incluídos."
                                placement="top"
                                arrow
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 12, color: '#B0BEC5', cursor: 'default' }} />
                              </Tooltip>
                            </Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'Inter', lineHeight: 1.4, color: rentColor(carteiraMetrics.rentPct) }}>
                              {formatRentSimples(carteiraMetrics.rentPct)}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', lineHeight: 1.2, color: rentColor(carteiraMetrics.varTotal) }}>
                              {formatBRL(carteiraMetrics.varTotal)}
                            </Typography>
                            {formatCdiSpread(carteiraMetrics.rentPct, data?.indicadores_diarios?.['CDI'] ?? null) && (
                              <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', lineHeight: 1.2, color: '#8A98A8', fontStyle: 'italic' }}>
                                {formatCdiSpread(carteiraMetrics.rentPct, data?.indicadores_diarios?.['CDI'] ?? null)}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                      {rentEsperada && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                                Rent. Esperada
                              </Typography>
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5, maxWidth: 300 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '12px', fontFamily: 'Inter', mb: 0.5 }}>
                                      Rentabilidade Esperada do Dia
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: '#CFD8DC', mb: 1 }}>
                                      Média ponderada pelo valor total da carteira. Ativos sem remuneração cadastrada contribuem com retorno zero.
                                      {rentEsperada && ` (${rentEsperada.ativosComExp}/${rentEsperada.totalAtivos} ativos com remuneração)`}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, fontSize: '11px', fontFamily: 'Inter', mb: 0.5 }}>
                                      Fórmula por tipo:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                      <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter' }}>Taxa Fixa (AA)</Typography>
                                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#90CAF9' }}>
                                          (1 + taxa)^(1/252) − 1
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter' }}>Taxa Fixa (AM)</Typography>
                                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#90CAF9' }}>
                                          (1 + taxa)^(1/21) − 1
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter' }}>% do Indicador</Typography>
                                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#90CAF9' }}>
                                          taxa_ind_diária × percentual
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter' }}>Indicador + Spread (AA)</Typography>
                                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#90CAF9' }}>
                                          (1 + ind) × (1 + spread)^(1/252) − 1
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Inter' }}>Indicador + Spread (AM)</Typography>
                                        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#90CAF9' }}>
                                          (1 + ind) × (1 + spread)^(1/21) − 1
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                }
                                placement="top"
                                arrow
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 12, color: '#B0BEC5', cursor: 'default' }} />
                              </Tooltip>
                            </Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'Inter', lineHeight: 1.4, color: '#4D5766' }}>
                              {formatRentSimples(rentEsperada.rentPct)}
                            </Typography>
                            {rentEsperada.rentBrl != null && (
                              <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', lineHeight: 1.2, color: '#6B7A8D' }}>
                                {formatBRL(rentEsperada.rentBrl)}
                              </Typography>
                            )}
                            {formatCdiPct(rentEsperada.cdiPct) && (
                              <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', lineHeight: 1.2, color: '#8A98A8', fontStyle: 'italic' }}>
                                {formatCdiPct(rentEsperada.cdiPct)}
                              </Typography>
                            )}
                            {formatCdiSpread(rentEsperada.rentPct, data?.indicadores_diarios?.['CDI'] ?? null) && (
                              <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', lineHeight: 1.2, color: '#8A98A8', fontStyle: 'italic' }}>
                                {formatCdiSpread(rentEsperada.rentPct, data?.indicadores_diarios?.['CDI'] ?? null)}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                      {/* pfee do dia — configurado em Passivos > Tipo = Taxa de Performance */}
                      {data.pfee_valor != null && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                            <Tooltip title={data.pfee_nome ?? ''} placement="top">
                              <Box>
                                <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                                  pfee do dia
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'Inter', lineHeight: 1.4, color: '#E51739' }}>
                                  {formatBRL(data.pfee_valor)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        </>
                      )}

                      {grouped && groups.length > 0 && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                            <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                              Agrupamentos
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2D3748', fontFamily: 'Inter', lineHeight: 1.4 }}>
                              {groups.length}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ px: 2, py: 0.75, backgroundColor: '#F8FAFC' }}>
                        <Typography sx={{ fontSize: '10px', color: '#8A98A8', fontFamily: 'Inter', lineHeight: 1.2 }}>
                          Patrimônio Líquido
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2C91AD', fontFamily: 'Inter', lineHeight: 1.4 }}>
                          {formatBRL(data.patrimonio_liquido)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  <CondominioBreakdownSection
                    ativos={data.ativos}
                    selected={condominioFiltro}
                    onSelect={setCondominioFiltro}
                    segmento={data.segmento}
                    indicadoresDiarios={data.indicadores_diarios}
                  />

                  <Box sx={{ minHeight: 320 }}>
                  {!grouped ? (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #CFD8E5', borderRadius: 2 }}>
                      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1.5 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...thSx, width: 70 }} />
                            <TableCell sx={{ ...thSx, width: 110, pl: 1 }}>Código</TableCell>
                            <TableCell sx={{ ...thSx, width: 130 }}>Nome</TableCell>
                            <TableCell sx={{ ...thSx, width: 110 }}>Classe</TableCell>
                            <TableCell sx={{ ...thSx, width: 55 }}>Subclasse</TableCell>
                            <TableCell sx={{ ...thSx, width: 95, textAlign: 'right' }}>Qtd. Cotas</TableCell>
                            <TableCell sx={{ ...thSx, width: 85, textAlign: 'right' }}>Valor da Cota</TableCell>
                            <TableCell sx={{ ...thSx, width: 110, textAlign: 'right' }}>Valor Financeiro</TableCell>
                            <TableCell sx={{ ...thSx, width: 90, textAlign: 'right' }}>Rentabilidade</TableCell>
                            <TableCell sx={{ ...thSx, width: 80, textAlign: 'right' }}>Concentração</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAtivos.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={NUM_COLS} align="center" sx={{ py: 4, color: '#8A98A8' }}>
                                {filtroTexto ? 'Nenhum ativo corresponde ao filtro.' : 'Nenhum ativo encontrado para esta data.'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAtivos.map((ativo: PosicaoAtualAtivoItem, i: number) => (
                              <AtivoTableRow
                                key={`row_${i}`}
                                ativo={ativo}
                                flatIndex={i}
                                fundoId={fundoId}
                                isExpanded={expandedRowKey === `row_${i}`}
                                onToggle={handleToggleRow}
                                onOpenCadastro={handleAbrirCadastro}
                              />
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {groups.length === 0 ? (
                        <Typography sx={{ py: 4, textAlign: 'center', color: '#8A98A8', fontSize: '13px' }}>
                          Nenhum ativo encontrado para esta data.
                        </Typography>
                      ) : (
                        groups.map(group => (
                          <AssetGroupCard
                            key={group.key}
                            group={group}
                            isExpanded={expandedGroups.has(group.key)}
                            onToggle={handleToggleGroup}
                            fundoId={fundoId}
                            expandedRowKey={expandedRowKey}
                            onToggleRow={handleToggleRow}
                            onOpenCadastro={handleAbrirCadastro}
                            indicadoresDiarios={data?.indicadores_diarios}
                          />
                        ))
                      )}
                    </Box>
                  )}
                  </Box>
                </>
              )}
          </TabPanel>

          {/* ── Tab: Rentabilidade vs Indicadores ────────────────────────── */}
          {rentTabVisited && (
            <RentabilidadeComparativaTab fundoId={fundoId} isVisible={mainTab === 'rentabilidade'} />
          )}

          {/* ── Tab: Histórico ───────────────────────────────────────────── */}
          <TabPanel value={mainTab} index="historico">
            <HistoricoFundoTab fundoId={fundoId} />
          </TabPanel>

          {/* ── Tab: Movimentações ───────────────────────────────────────── */}
          <TabPanel value={mainTab} index="movimentacoes">
            <MovimentacoesCotistaTab fundoId={fundoId} />
          </TabPanel>

          {/* ── Tab: Cronograma ──────────────────────────────────────────── */}
          <TabPanel value={mainTab} index="cronograma">
            {loadingCronogramas && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}
            {!loadingCronogramas && Object.keys(cronogramaMap).length === 0 && (
              <Typography sx={{ py: 4, textAlign: 'center', color: '#8A98A8', fontSize: '13px' }}>
                Nenhum ativo com condomínio Fechado e cronograma cadastrado na carteira.
              </Typography>
            )}
            {!loadingCronogramas && Object.keys(cronogramaMap).length > 0 && (
              <CronogramaCalendarView
                cronogramaMap={cronogramaMap}
                fechadoNomes={fechadoNomes}
                fechadoDetails={fechadoDetails}
              />
            )}
          </TabPanel>

          <Button onClick={onRequestClose} variant="contained" sx={{ mt: 3 }}>
            Fechar
          </Button>
        </Box>
      </Modal>

      {ativoParaCadastrar && (
        <CadastrarAtivoMasterModal
          isOpen={cadastroModalOpen}
          onRequestClose={() => setCadastroModalOpen(false)}
          dadosCarteira={{
            administradora: ativoParaCadastrar.adm,
            codigo_bruto: ativoParaCadastrar.codigo1 ?? '',
            nome_bruto: ativoParaCadastrar.nome,
          }}
          onSalvo={handleCadastroSalvo}
        />
      )}
    </>
  );
};

export default CarteiraPosicaoModal;
